
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { invokeWithRetry } from "@/services/edgeFunctionWrappers/common/invokeWithRetry";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Card, CardHeader, CardContent, CardTitle, CardDescription } from "@/components/ui/card";
import { toast } from "sonner";
import { useAppName } from "@/hooks/useAppName";

export default function ForgotPasswordPage() {
  const { t } = useTranslation(["auth", "common"]);
  const navigate = useNavigate();
  const appInfo = useAppName();

  const [email, setEmail] = useState("");
  const [status, setStatus] = useState<"idle" | "success" | "error">("idle");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [retryAttempt, setRetryAttempt] = useState(0);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setStatus("idle");
    setRetryAttempt(0);

    try {
      console.log('[ForgotPassword] Sending password reset email to:', email);
      
      // Page atteignable SANS session : `skipSessionHandling` coupe le
      // rafraîchissement de jeton, le `signOut` et la redirection vers /login
      // que le wrapper commun applique sur 401 — ici il n'y a pas de session à
      // rafraîchir, et rediriger depuis un écran public boucle sur lui-même.
      const { data, error } = await invokeWithRetry<{ success: boolean; error?: string }>(
        'send-password-reset',
        { email, appName: appInfo },
        2, // max 2 retries (3 total attempts)
        {
          timeoutMs: 15000, // 15s timeout per attempt
          skipSessionHandling: true,
          retryOnServerError: true, // l'envoi d'e-mail échoue en 500, et rejouer est sans effet de bord
          // Seul site du dépôt à rejouer un timeout : c'est le comportement de
          // la copie privée que cette page utilisait, et le pire cas est un
          // e-mail de réinitialisation reçu en double — pas une ligne dupliquée
          // en base. Le défaut (`false`) reste en vigueur partout ailleurs.
          retryOnTimeout: true,
          onRetry: (attempt) => setRetryAttempt(attempt),
        }
      );

      if (error || !data?.success) {
        console.error('[ForgotPassword] Error sending reset email:', error || data?.error);
        setStatus("error");
        toast.error(t("emailError", { ns: "auth" }));
      } else {
        console.log('[ForgotPassword] Reset email sent successfully');
        setStatus("success");
        toast.success(t("emailSent", { ns: "auth" }));
        
        setTimeout(() => {
          navigate("/login");
        }, 3000);
      }
    } catch (error) {
      console.error('[ForgotPassword] Unexpected error:', error);
      setStatus("error");
      toast.error(t("emailError", { ns: "auth" }));
    } finally {
      setIsSubmitting(false);
      setRetryAttempt(0);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-4 pb-[calc(env(safe-area-inset-bottom)+1rem)] pt-[calc(env(safe-area-inset-top)+1rem)]">
      <Card className="w-full max-w-md shadow-md">
        <CardHeader>
          <CardTitle>{t("forgotPassword", { ns: "auth" })}</CardTitle>
          <CardDescription>{t("forgotPasswordDescription", { ns: "auth" })}</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label htmlFor="email">{t("email", { ns: "auth" })}</Label>
              <Input
                id="email"
                type="email"
                placeholder="you@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                autoComplete="email"
                required
              />
            </div>

            <Button
              type="submit"
              className="w-full bg-amber-500 hover:bg-amber-600"
              disabled={isSubmitting}
            >
              {isSubmitting 
                ? (retryAttempt > 0 
                    ? `Reconnexion... (${retryAttempt + 1}/3)` 
                    : t("loading", { ns: "common" }))
                : t("sendResetLink", { ns: "auth" })}
            </Button>

            {status === "success" && (
              <div className="p-3 bg-green-50 border border-green-200 rounded-md">
                <p className="text-sm text-green-700">
                  {t("emailSent", { ns: "auth" })}
                </p>
                <p className="text-xs text-green-600 mt-1">
                  Redirection vers la page de connexion dans 3 secondes...
                </p>
              </div>
            )}

            {status === "error" && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-md">
                <p className="text-sm text-red-700">
                  {t("emailError", { ns: "auth" })}
                </p>
              </div>
            )}

            <div className="text-center">
              <Button
                type="button"
                variant="ghost"
                onClick={() => navigate("/login")}
                className="text-sm text-gray-600 hover:text-gray-800"
              >
                {t("backToHome", { ns: "auth" })}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}